import React, { useState } from 'react';
import {
  ArrowLeft,
  Search,
  ChevronDown,
  FileText,
  Download,
  Upload,
  X,
  Plus,
  Eye,
  Check,
  AlertCircle,
  Car,
  Bike,
  FileSpreadsheet,
} from 'lucide-react';
import { ResidentSubMenuKey } from '../ResidentDirectory';

interface Props {
  onBack: () => void;
  onNavigate?: (tab: ResidentSubMenuKey) => void;
}

export interface VehicleRecord {
  id: string;
  roomNumber: string;
  plateNumber: string;
  province?: string;
  vehicleType: 'รถยนต์' | 'รถจักรยานยนต์';
  brandModel: string;
  carImage?: string;
  carImageAlt?: string;
  carIconType?: 'bmw' | 'car_blue' | 'bike' | 'none';
  characteristics: string; // e.g. "ดำ", "ล้อหลุด เบรกทรุด คันเกียร์หาย แบตเตอรี่เสื่อม"
  stickerNumber: string; // e.g. "444444", "AVC4456689", "1234"
  hasParkingSticker: boolean; // boolean checkmark (✓)
  parkingFloor: string; // e.g. "-", "2"
  parkingSlot: string; // e.g. "-", "10"
  year?: string;
  seats?: string;
  startDate?: string;
  chargeParkingFee?: boolean;
}

const INITIAL_VEHICLES: VehicleRecord[] = [
  {
    id: 'v-1',
    roomNumber: '1086/01',
    plateNumber: '1113',
    province: 'กรุงเทพมหานคร',
    vehicleType: 'รถยนต์',
    brandModel: 'BMW BMW 4 Series Coupe',
    carIconType: 'bmw',
    characteristics: 'ดำ',
    stickerNumber: '444444',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
    startDate: '2024-01-05',
  },
  {
    id: 'v-2',
    roomNumber: '1086/01',
    plateNumber: 'รฟ 3453',
    province: 'ชลบุรี',
    vehicleType: 'รถยนต์',
    brandModel: 'Honda City Rs',
    carIconType: 'car_blue',
    characteristics: 'ดำ',
    stickerNumber: 'AVC4456689',
    hasParkingSticker: true,
    parkingFloor: '2',
    parkingSlot: '10',
    year: '2019',
    seats: '7',
    startDate: '2024-01-05',
    chargeParkingFee: true,
  },
  {
    id: 'v-3',
    roomNumber: '1086/01',
    plateNumber: '2กด5555555555',
    province: 'กรุงเทพมหานคร',
    vehicleType: 'รถยนต์',
    brandModel: 'Toyota',
    carIconType: 'none',
    characteristics: '-',
    stickerNumber: '-',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
  },
  {
    id: 'v-4',
    roomNumber: '1086/01',
    plateNumber: 'test',
    province: 'ชลบุรี',
    vehicleType: 'รถจักรยานยนต์',
    brandModel: 'Yamaha Finn',
    carIconType: 'none',
    characteristics: 'test',
    stickerNumber: 'test',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
  },
  {
    id: 'v-5',
    roomNumber: '1086/01',
    plateNumber: '2กด5622',
    province: 'กรุงเทพมหานคร',
    vehicleType: 'รถยนต์',
    brandModel: 'Mitsubishi',
    carIconType: 'none',
    characteristics: '-',
    stickerNumber: '-',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
  },
  {
    id: 'v-6',
    roomNumber: '1086/01',
    plateNumber: '1กก2445',
    province: 'กรุงเทพมหานคร',
    vehicleType: 'รถยนต์',
    brandModel: 'BMW BMW 5 Series Sedan',
    carIconType: 'none',
    characteristics: 'ล้อหลุด เบรกทรุด คันเกียร์หาย แบตเตอรี่เสื่อม',
    stickerNumber: '1234',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
  },
  {
    id: 'v-7',
    roomNumber: '1086/01',
    plateNumber: '9กด 2300',
    province: 'กรุงเทพมหานคร',
    vehicleType: 'รถยนต์',
    brandModel: 'BMW BMW 4 Series Coupe',
    carIconType: 'none',
    characteristics: 'สีดำ',
    stickerNumber: '231',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
  },
  {
    id: 'v-8',
    roomNumber: '1086/01',
    plateNumber: '1377',
    province: 'กรุงเทพมหานคร',
    vehicleType: 'รถยนต์',
    brandModel: 'Honda HR-V',
    carIconType: 'none',
    characteristics: '-',
    stickerNumber: '010',
    hasParkingSticker: true,
    parkingFloor: '-',
    parkingSlot: '-',
  },
  {
    id: 'v-9',
    roomNumber: '1086/02',
    plateNumber: 'หก4475',
    province: 'ชลบุรี',
    vehicleType: 'รถจักรยานยนต์',
    brandModel: 'Yamaha gh',
    carIconType: 'none',
    characteristics: '-',
    stickerNumber: 'MC-2024-001',
    hasParkingSticker: true,
    parkingFloor: '1',
    parkingSlot: 'M05',
    startDate: '2024-01-06',
  },
];

export const ResidentVehiclesView: React.FC<Props> = ({ onBack, onNavigate }) => {
  const [vehicles, setVehicles] = useState<VehicleRecord[]>(INITIAL_VEHICLES);
  const [activeFilterTab, setActiveFilterTab] = useState<'all' | 'car' | 'motorcycle'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExcelTemplateModal, setShowExcelTemplateModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Import form state
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [importSuccessMsg, setImportSuccessMsg] = useState('');

  // Top navigation tabs
  const topTabs: { key: ResidentSubMenuKey; label: string }[] = [
    { key: 'members', label: 'เจ้าของร่วม/สมาชิก' },
    { key: 'vehicles', label: 'ยานพาหนะ' },
    { key: 'keycards', label: 'คีย์การ์ด' },
    { key: 'foreign_ownership', label: 'กรรมสิทธิ์ชาวต่างชาติ' },
    { key: 'pets', label: 'สัตว์เลี้ยง' },
    { key: 'title_deeds', label: 'กรรมสิทธิ์' },
    { key: 'history_logs', label: 'ประวัติผู้อยู่อาศัย' },
  ];

  // Filtering
  const filteredVehicles = vehicles.filter((v) => {
    // Type filter
    if (activeFilterTab === 'car' && v.vehicleType !== 'รถยนต์') return false;
    if (activeFilterTab === 'motorcycle' && v.vehicleType !== 'รถจักรยานยนต์') return false;

    // Search query filter
    const q = appliedSearch.toLowerCase().trim();
    if (!q) return true;

    return (
      v.roomNumber.toLowerCase().includes(q) ||
      v.plateNumber.toLowerCase().includes(q) ||
      v.brandModel.toLowerCase().includes(q) ||
      v.characteristics.toLowerCase().includes(q) ||
      v.stickerNumber.toLowerCase().includes(q) ||
      (v.parkingFloor && v.parkingFloor.toLowerCase().includes(q)) ||
      (v.parkingSlot && v.parkingSlot.toLowerCase().includes(q))
    );
  });

  // Group by room for styling
  const uniqueRooms = Array.from(new Set(filteredVehicles.map((v) => v.roomNumber)));

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchQuery);
  };

  const handleExportPDF = () => {
    setShowPrintDropdown(false);
    window.print();
  };

  const handleExportExcel = () => {
    setShowPrintDropdown(false);
    let csv =
      'ลำดับ,เลขบ้านเลขที่/ห้องเลขที่,ประเภท,เลขทะเบียน,จังหวัด,ยี่ห้อ,รุ่น,ปี,ขนาด(ที่นั่ง),ลักษณะรถ,เลขสติ๊กเกอร์จอดรถ,ชั้นจอด,ช่องที่จอด,วันที่เริ่มใช้งาน,คิดค่าที่จอดรถ\n';
    vehicles.forEach((v, index) => {
      const parts = v.brandModel.split(' ');
      const brand = parts[0] || v.brandModel;
      const model = parts.slice(1).join(' ') || '-';
      csv += `${index + 1},"${v.roomNumber}","${v.vehicleType}","${v.plateNumber}","${
        v.province || 'ชลบุรี'
      }","${brand}","${model}","${v.year || ''}","${v.seats || ''}","${
        v.characteristics === '-' ? '' : v.characteristics
      }","${v.stickerNumber === '-' ? '' : v.stickerNumber}","${
        v.parkingFloor === '-' ? '' : v.parkingFloor
      }","${v.parkingSlot === '-' ? '' : v.parkingSlot}","${
        v.startDate || '2024-01-05'
      }","${v.chargeParkingFee ? '/' : ''}"\n`;
    });

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ข้อมูลยานพาหนะ_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Download Sample Excel Template
  const handleDownloadTemplate = () => {
    let csv =
      'ลำดับ,เลขบ้านเลขที่/ห้องเลขที่,ประเภท,เลขทะเบียน,จังหวัด,ยี่ห้อ,รุ่น,ปี,ขนาด (ที่นั่ง),ลักษณะรถ,เลขสติ๊กเกอร์จอดรถ,ชั้นจอด,ช่องที่จอด,วันที่เริ่มใช้งาน,คิดค่าที่จอดรถ\n';
    csv += '1,1086/01,รถยนต์,รฟ 3453,ชลบุรี,Honda,mkl,2019,7,ดำ,AVC4456689,2,10,2024-01-05,/\n';
    csv += '2,1086/02,รถจักรยานยนต์,หก4475,ชลบุรี,Yamaha,gh,,,,,,,2024-01-06,\n';

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'แบบฟอร์มนำเข้ายานพาหนะ_Template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle Mock Import File
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFileName(file.name);
    }
  };

  const handleConfirmImport = () => {
    if (!uploadedFileName) {
      alert('กรุณาเลือกไฟล์ Excel (.xlsx, .csv) ก่อนกดยืนยัน');
      return;
    }

    // Add imported samples if imported
    const newItems: VehicleRecord[] = [
      {
        id: `v-imp-1-${Date.now()}`,
        roomNumber: '1086/05',
        plateNumber: '3กค 8899',
        province: 'ชลบุรี',
        vehicleType: 'รถยนต์',
        brandModel: 'Honda Civic e:HEV',
        carIconType: 'car_blue',
        characteristics: 'สีขาวมุก',
        stickerNumber: 'AVC4459901',
        hasParkingSticker: true,
        parkingFloor: '3',
        parkingSlot: '14',
        startDate: '2026-05-01',
      },
    ];

    setVehicles([...vehicles, ...newItems]);
    setShowImportModal(false);
    setUploadedFileName('');
    setImportSuccessMsg('นำเข้าข้อมูลสำเร็จ 1 รายการ');
    setTimeout(() => setImportSuccessMsg(''), 4000);
  };

  return (
    <div className="font-prompt bg-white min-h-[85vh] rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
      {/* 1. Top Sub-Navigation Tabs matching screenshots */}
      <div className="border-b border-slate-200 bg-slate-50/50 px-4 sm:px-6 pt-3 overflow-x-auto">
        <div className="flex items-center gap-6 sm:gap-8 min-w-max text-xs sm:text-sm">
          {topTabs.map((tab) => {
            const isActive = tab.key === 'vehicles';
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (tab.key === 'vehicles') return;
                  if (onNavigate) {
                    onNavigate(tab.key);
                  } else {
                    onBack();
                  }
                }}
                className={`pb-3 font-medium transition-colors cursor-pointer relative ${
                  isActive
                    ? 'text-slate-900 font-bold border-b-2 border-teal-600'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-5">
        {/* Success toast banner if imported */}
        {importSuccessMsg && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs flex items-center justify-between animate-in fade-in">
            <span className="font-semibold flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-600" /> {importSuccessMsg}
            </span>
            <button onClick={() => setImportSuccessMsg('')} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* 2. Header Bar: Title + Import Button + Print Button */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* Left: Back Button & Page Title */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="w-8 h-8 rounded-full border border-slate-300 hover:border-slate-400 bg-white flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors shadow-2xs cursor-pointer"
              title="ย้อนกลับไปเมนูหลัก"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
              ข้อมูลยานพาหนะ
            </h1>
          </div>

          {/* Right: Import & Print buttons */}
          <div className="flex items-center gap-2.5 justify-end">
            {/* Blue Import Button matching image */}
            <button
              type="button"
              onClick={() => setShowImportModal(true)}
              className="px-5 py-2 bg-[#0066cc] hover:bg-[#0052a3] text-white rounded-md text-xs font-semibold flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
            >
              Import
            </button>

            {/* Print Dropdown Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                className="px-3.5 py-2 bg-white border border-[#3b82f6] text-[#2563eb] hover:bg-blue-50/60 rounded-md text-xs font-medium flex items-center gap-1.5 shadow-2xs transition-colors cursor-pointer"
              >
                <span>พิมพ์</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {/* Dropdown Menu */}
              {showPrintDropdown && (
                <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-30 text-xs animate-in fade-in zoom-in-95">
                  <button
                    type="button"
                    onClick={handleExportPDF}
                    className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
                  >
                    <FileText className="w-3.5 h-3.5 text-rose-500" />
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={handleExportExcel}
                    className="w-full px-3 py-2 text-left text-slate-700 hover:bg-slate-50 flex items-center gap-2 cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5 text-emerald-600" />
                    Excel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 3. Sub Filter Tabs (ยานพาหนะทั้งหมด / รถยนต์ / รถจักรยานยนต์) & Search Bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-1">
          {/* Sub tabs with blue active indicator matching image */}
          <div className="flex items-center gap-6 text-xs sm:text-sm">
            <button
              type="button"
              onClick={() => setActiveFilterTab('all')}
              className={`pb-2 font-medium transition-colors cursor-pointer relative ${
                activeFilterTab === 'all'
                  ? 'text-slate-900 font-bold border-b-2 border-[#0066cc]'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              ยานพาหนะทั้งหมด
            </button>
            <button
              type="button"
              onClick={() => setActiveFilterTab('car')}
              className={`pb-2 font-medium transition-colors cursor-pointer relative ${
                activeFilterTab === 'car'
                  ? 'text-slate-900 font-bold border-b-2 border-[#0066cc]'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              รถยนต์
            </button>
            <button
              type="button"
              onClick={() => setActiveFilterTab('motorcycle')}
              className={`pb-2 font-medium transition-colors cursor-pointer relative ${
                activeFilterTab === 'motorcycle'
                  ? 'text-slate-900 font-bold border-b-2 border-[#0066cc]'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              รถจักรยานยนต์
            </button>
          </div>

          {/* Search Box with blue Search button */}
          <form onSubmit={handleSearch} className="flex items-center self-end md:self-auto">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="ค้นหา"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 bg-white border border-slate-300 rounded-l-md text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 w-36 sm:w-48"
              />
            </div>
            <button
              type="submit"
              className="px-3.5 py-1.5 bg-[#0066cc] hover:bg-[#0052a3] text-white text-xs font-semibold rounded-r-md border border-[#0066cc] shadow-2xs transition-colors cursor-pointer"
            >
              ค้นหา
            </button>
          </form>
        </div>

        {/* 4. Main Vehicles Table matching exact layout, headers, and rows from image */}
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-2xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-[#eef2f6] text-slate-700 font-semibold border-b border-slate-200 text-xs">
                  <th className="py-3 px-3 sm:px-4 w-24 text-slate-700 font-medium">ห้อง</th>
                  <th className="py-3 px-3 sm:px-4 w-32 text-slate-700 font-medium">เลขทะเบียน</th>
                  <th className="py-3 px-3 sm:px-4 w-28 text-slate-700 font-medium">ประเภท</th>
                  <th className="py-3 px-3 sm:px-4 text-slate-700 font-medium min-w-[180px]">
                    ยี่ห้อ/รุ่น
                  </th>
                  <th className="py-3 px-2 sm:px-3 w-16 text-center text-slate-700 font-medium">
                    รูปรถ
                  </th>
                  <th className="py-3 px-3 sm:px-4 text-slate-700 font-medium min-w-[160px]">
                    ลักษณะรถ
                  </th>
                  <th className="py-3 px-3 sm:px-4 w-28 text-slate-700 font-medium">เลขสติ๊กเกอร์</th>
                  <th className="py-3 px-2 sm:px-3 w-32 text-center text-slate-700 font-medium">
                    เลขสติ๊กเกอร์จอดรถ
                  </th>
                  <th className="py-3 px-2 sm:px-3 w-20 text-center text-slate-700 font-medium">
                    ชั้นจอด
                  </th>
                  <th className="py-3 px-2 sm:px-3 w-24 text-center text-slate-700 font-medium">
                    ช่องที่จอด
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredVehicles.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-12 text-center text-slate-400">
                      ไม่พบข้อมูลยานพาหนะที่ตรงกับเงื่อนไขการค้นหา
                    </td>
                  </tr>
                ) : (
                  uniqueRooms.map((room, roomIdx) => {
                    const roomVehicles = filteredVehicles.filter((v) => v.roomNumber === room);

                    return roomVehicles.map((vehicle, vIdx) => {
                      const isFirstInRoom = vIdx === 0;

                      return (
                        <tr
                          key={vehicle.id}
                          className={`hover:bg-slate-50/80 transition-colors ${
                            isFirstInRoom && roomIdx !== 0 ? 'border-t-2 border-slate-200' : ''
                          }`}
                        >
                          {/* Room Number Column (Rowspan grouped effect) */}
                          <td className="py-3.5 px-3 sm:px-4 align-top">
                            {isFirstInRoom ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setSearchQuery(vehicle.roomNumber);
                                  setAppliedSearch(vehicle.roomNumber);
                                }}
                                className="font-semibold text-[#0066cc] hover:underline font-mono text-xs cursor-pointer block"
                                title={`คลิกเพื่อกรองห้อง ${vehicle.roomNumber}`}
                              >
                                {vehicle.roomNumber}
                              </button>
                            ) : null}
                          </td>

                          {/* Plate Number */}
                          <td className="py-3.5 px-3 sm:px-4 align-middle font-medium text-slate-800">
                            {vehicle.plateNumber}
                          </td>

                          {/* Vehicle Type (รถยนต์ / รถจักรยานยนต์) */}
                          <td className="py-3.5 px-3 sm:px-4 align-middle">
                            {vehicle.vehicleType === 'รถจักรยานยนต์' ? (
                              <span className="px-1.5 py-0.5 rounded-sm bg-blue-600 text-white font-medium text-[11px]">
                                {vehicle.vehicleType}
                              </span>
                            ) : (
                              <span className="text-slate-800">{vehicle.vehicleType}</span>
                            )}
                          </td>

                          {/* Brand / Model */}
                          <td className="py-3.5 px-3 sm:px-4 align-middle font-medium text-slate-800">
                            {vehicle.brandModel}
                          </td>

                          {/* Car Image Thumbnail / Icon */}
                          <td className="py-3.5 px-2 sm:px-3 align-middle text-center">
                            {vehicle.carIconType === 'bmw' ? (
                              <div className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 border border-slate-300 text-[9px] font-bold text-slate-700">
                                BMW
                              </div>
                            ) : vehicle.carIconType === 'car_blue' ? (
                              <div className="inline-flex items-center justify-center text-[#2563eb]">
                                <svg
                                  className="w-5 h-5 fill-none stroke-current stroke-2"
                                  viewBox="0 0 24 24"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.8C2.1 11 2 11.5 2 12v4c0 .6.4 1 1 1h2" />
                                  <circle cx="7" cy="17" r="2" />
                                  <path d="M9 17h6" />
                                  <circle cx="17" cy="17" r="2" />
                                </svg>
                              </div>
                            ) : (
                              <span className="text-slate-400 font-mono">-</span>
                            )}
                          </td>

                          {/* Characteristics */}
                          <td className="py-3.5 px-3 sm:px-4 align-middle text-slate-700">
                            {vehicle.characteristics}
                          </td>

                          {/* Sticker Number */}
                          <td className="py-3.5 px-3 sm:px-4 align-middle font-mono text-slate-800">
                            {vehicle.stickerNumber}
                          </td>

                          {/* Parking Sticker Checkmark (✓) */}
                          <td className="py-3.5 px-2 sm:px-3 align-middle text-center">
                            {vehicle.hasParkingSticker ? (
                              <span className="font-semibold text-slate-700 text-sm">✓</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>

                          {/* Parking Floor */}
                          <td className="py-3.5 px-2 sm:px-3 align-middle text-center font-mono text-slate-700">
                            {vehicle.parkingFloor}
                          </td>

                          {/* Parking Slot */}
                          <td className="py-3.5 px-2 sm:px-3 align-middle text-center font-mono text-slate-700">
                            {vehicle.parkingSlot}
                          </td>
                        </tr>
                      );
                    });
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer Summary / Count */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
          <span>
            แสดงยานพาหนะทั้งหมด{' '}
            <strong className="text-slate-800">{filteredVehicles.length}</strong> คัน (รถยนต์{' '}
            {filteredVehicles.filter((v) => v.vehicleType === 'รถยนต์').length} คัน, รถจักรยานยนต์{' '}
            {filteredVehicles.filter((v) => v.vehicleType === 'รถจักรยานยนต์').length} คัน)
          </span>
          <span className="text-slate-400">โครงการ ออร์คิด พาร์ค วิลล์</span>
        </div>
      </div>

      {/* 5. Modal: Import File (Matching Image 2 exactly) */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 my-8 text-xs">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white">
              <h2 className="text-base font-bold text-slate-800">Import File</h2>
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(false);
                  setUploadedFileName('');
                }}
                className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Info & Example template link */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 text-slate-600">
                  <span className="w-4 h-4 rounded-full border border-slate-400 flex items-center justify-center text-[10px] text-slate-500 font-bold shrink-0">
                    i
                  </span>
                  <span>โปรดเลือกไฟล์ ประเภท Excel เท่านั้น ไฟล์ขนาดไม่เกิน 10 MB</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowExcelTemplateModal(true)}
                    className="text-[#0066cc] hover:underline font-semibold cursor-pointer"
                  >
                    ตัวอย่างแบบฟอร์ม (ดูหน้าตา Excel)
                  </button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="text-emerald-700 hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                  >
                    <Download className="w-3 h-3" /> ดาวน์โหลด Template (.csv)
                  </button>
                </div>
              </div>

              {/* File Input Box with Browse button */}
              <div className="flex items-center border border-slate-300 rounded-md overflow-hidden bg-white shadow-2xs">
                <input
                  type="text"
                  readOnly
                  value={uploadedFileName}
                  placeholder=""
                  className="w-full px-3 py-2 text-xs text-slate-700 bg-white focus:outline-none"
                />
                <label className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold border-l border-slate-300 cursor-pointer transition-colors shrink-0">
                  Browse
                  <input
                    type="file"
                    accept=".xlsx, .xls, .csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              </div>

              {/* Required Legend */}
              <div className="flex items-center gap-2 pt-1">
                <div className="w-3.5 h-3.5 bg-[#e03131] rounded-[2px]" />
                <span className="text-slate-700 font-medium">
                  คือฟิลด์บังคับ ต้องระบุข้อมูล ให้ถูกต้อง
                </span>
              </div>

              {/* Import Schema Columns Preview matching image 2 */}
              <div className="border border-slate-200 rounded-lg overflow-x-auto bg-slate-50/50">
                <table className="w-full text-center text-[11px] border-collapse min-w-[900px]">
                  <thead>
                    <tr className="bg-[#eef2f6] text-slate-700 border-b border-slate-200">
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">แถวที่</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">ลำดับ</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        เลขบ้านเลขที่/ห้องเลขที่*
                      </th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        ประเภท*
                      </th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        เลขทะเบียน*
                      </th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        จังหวัด*
                      </th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        ยี่ห้อ*
                      </th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        รุ่น*
                      </th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">ปี</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">ขนาด (ที่นั่ง)</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">ลักษณะรถ</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">เลขสติ๊กเกอร์จอดรถ</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">ชั้นจอด</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal">ช่องที่จอด</th>
                      <th className="py-2.5 px-2 border-r border-slate-200 font-normal text-white bg-[#e03131]">
                        <div>วันที่เริ่มใช้งาน*</div>
                        <div className="text-[9px] font-normal">YYYY-MM-DD เท่านั้น</div>
                      </th>
                      <th className="py-2.5 px-2 font-normal">คิดค่าที่จอดรถ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white text-slate-600">
                      <td className="py-3 px-2 border-r border-slate-200 font-mono">1</td>
                      <td className="py-3 px-2 border-r border-slate-200 font-mono">1</td>
                      <td className="py-3 px-2 border-r border-slate-200 font-mono text-blue-600 font-semibold">
                        1086/01
                      </td>
                      <td className="py-3 px-2 border-r border-slate-200">รถยนต์</td>
                      <td className="py-3 px-2 border-r border-slate-200">รฟ 3453</td>
                      <td className="py-3 px-2 border-r border-slate-200">ชลบุรี</td>
                      <td className="py-3 px-2 border-r border-slate-200">Honda</td>
                      <td className="py-3 px-2 border-r border-slate-200">mkl</td>
                      <td className="py-3 px-2 border-r border-slate-200">2019</td>
                      <td className="py-3 px-2 border-r border-slate-200">7</td>
                      <td className="py-3 px-2 border-r border-slate-200">ดำ</td>
                      <td className="py-3 px-2 border-r border-slate-200">AVC4456689</td>
                      <td className="py-3 px-2 border-r border-slate-200">2</td>
                      <td className="py-3 px-2 border-r border-slate-200">10</td>
                      <td className="py-3 px-2 border-r border-slate-200 font-mono">2024-01-05</td>
                      <td className="py-3 px-2 font-bold text-slate-800">/</td>
                    </tr>
                    <tr className="bg-slate-50/70 text-slate-600">
                      <td className="py-3 px-2 border-r border-slate-200 font-mono">2</td>
                      <td className="py-3 px-2 border-r border-slate-200 font-mono">2</td>
                      <td className="py-3 px-2 border-r border-slate-200 font-mono text-blue-600 font-semibold">
                        1086/02
                      </td>
                      <td className="py-3 px-2 border-r border-slate-200">รถจักรยานยนต์</td>
                      <td className="py-3 px-2 border-r border-slate-200">หก4475</td>
                      <td className="py-3 px-2 border-r border-slate-200">ชลบุรี</td>
                      <td className="py-3 px-2 border-r border-slate-200">Yamaha</td>
                      <td className="py-3 px-2 border-r border-slate-200">gh</td>
                      <td className="py-3 px-2 border-r border-slate-200">-</td>
                      <td className="py-3 px-2 border-r border-slate-200">-</td>
                      <td className="py-3 px-2 border-r border-slate-200">-</td>
                      <td className="py-3 px-2 border-r border-slate-200">-</td>
                      <td className="py-3 px-2 border-r border-slate-200">-</td>
                      <td className="py-3 px-2 border-r border-slate-200">-</td>
                      <td className="py-3 px-2 border-r border-slate-200 font-mono">2024-01-06</td>
                      <td className="py-3 px-2">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer Actions */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(false);
                  setUploadedFileName('');
                }}
                className="px-5 py-2 rounded-md bg-[#868e96] hover:bg-[#727b84] text-white text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
              >
                ยกเลิก
              </button>
              <button
                type="button"
                onClick={handleConfirmImport}
                className="px-6 py-2 rounded-md bg-[#0066cc] hover:bg-[#0052a3] text-white text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
              >
                ยืนยัน
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 6. Modal: Excel Template Mockup matching Image 3 */}
      {showExcelTemplateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-in fade-in overflow-y-auto">
          <div className="bg-white rounded-xl max-w-5xl w-full shadow-2xl border border-slate-300 overflow-hidden animate-in zoom-in-95 my-6 text-xs">
            {/* Excel Window Header */}
            <div className="bg-[#107c41] text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-white" />
                <span className="font-bold">
                  ตัวอย่างไฟล์ Excel (แบบฟอร์มนำเข้าข้อมูลยานพาหนะ.xlsx)
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowExcelTemplateModal(false)}
                className="text-white hover:text-slate-200 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Excel Grid */}
            <div className="p-5 space-y-4">
              <div className="border border-slate-300 rounded-md overflow-x-auto shadow-xs font-mono">
                <table className="w-full text-xs text-left border-collapse min-w-[950px]">
                  <thead>
                    <tr className="bg-[#e03131] text-white text-center font-semibold border-b border-slate-300">
                      <th className="p-2 border-r border-white/20 w-12">ลำดับ</th>
                      <th className="p-2 border-r border-white/20">เลขบ้านเลขที่/ห้องเลขที่</th>
                      <th className="p-2 border-r border-white/20">ประเภท</th>
                      <th className="p-2 border-r border-white/20">เลขทะเบียน</th>
                      <th className="p-2 border-r border-white/20">จังหวัด</th>
                      <th className="p-2 border-r border-white/20">ยี่ห้อ</th>
                      <th className="p-2 border-r border-white/20">รุ่น</th>
                      <th className="p-2 border-r border-white/20 bg-slate-100 text-slate-700">ปี</th>
                      <th className="p-2 border-r border-white/20 bg-slate-100 text-slate-700">
                        ขนาด (ที่นั่ง)
                      </th>
                      <th className="p-2 border-r border-white/20 bg-slate-100 text-slate-700">
                        ลักษณะรถ
                      </th>
                      <th className="p-2 border-r border-white/20 bg-slate-100 text-slate-700">
                        เลขสติ๊กเกอร์จอด
                      </th>
                      <th className="p-2 border-r border-white/20 bg-slate-100 text-slate-700">
                        ชั้นจอด
                      </th>
                      <th className="p-2 border-r border-white/20 bg-slate-100 text-slate-700">
                        ช่องที่จอด
                      </th>
                      <th className="p-2 border-r border-white/20">วันที่เริ่มใช้งาน</th>
                      <th className="p-2 bg-slate-100 text-slate-700">คิดค่าที่จอดรถ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    <tr className="hover:bg-slate-50 text-center">
                      <td className="p-2 border-r border-slate-200">1</td>
                      <td className="p-2 border-r border-slate-200 font-bold text-blue-700">1086/01</td>
                      <td className="p-2 border-r border-slate-200">รถยนต์</td>
                      <td className="p-2 border-r border-slate-200">รฟ 3453</td>
                      <td className="p-2 border-r border-slate-200">ชลบุรี</td>
                      <td className="p-2 border-r border-slate-200">Honda</td>
                      <td className="p-2 border-r border-slate-200">mkl</td>
                      <td className="p-2 border-r border-slate-200">2019</td>
                      <td className="p-2 border-r border-slate-200">7</td>
                      <td className="p-2 border-r border-slate-200">ดำ</td>
                      <td className="p-2 border-r border-slate-200">AVC4456689</td>
                      <td className="p-2 border-r border-slate-200">2</td>
                      <td className="p-2 border-r border-slate-200">10</td>
                      <td className="p-2 border-r border-slate-200">2024-01-05</td>
                      <td className="p-2 font-bold text-slate-800">/</td>
                    </tr>
                    <tr className="hover:bg-slate-50 text-center">
                      <td className="p-2 border-r border-slate-200">2</td>
                      <td className="p-2 border-r border-slate-200 font-bold text-blue-700">1086/02</td>
                      <td className="p-2 border-r border-slate-200">รถจักรยานยนต์</td>
                      <td className="p-2 border-r border-slate-200">หก4475</td>
                      <td className="p-2 border-r border-slate-200">ชลบุรี</td>
                      <td className="p-2 border-r border-slate-200">Yamaha</td>
                      <td className="p-2 border-r border-slate-200">gh</td>
                      <td className="p-2 border-r border-slate-200"></td>
                      <td className="p-2 border-r border-slate-200"></td>
                      <td className="p-2 border-r border-slate-200"></td>
                      <td className="p-2 border-r border-slate-200"></td>
                      <td className="p-2 border-r border-slate-200"></td>
                      <td className="p-2 border-r border-slate-200"></td>
                      <td className="p-2 border-r border-slate-200">2024-01-06</td>
                      <td className="p-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Guidelines & Notes matching Image 3 */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-2 text-xs">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 bg-[#e03131] rounded-[2px]" />
                    <span className="font-semibold text-slate-700">จำเป็นต้องกรอก</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3.5 h-3.5 bg-white border border-slate-300 rounded-[2px]" />
                    <span className="text-slate-600">ไม่จำเป็นต้องกรอก</span>
                  </div>
                </div>

                <div className="space-y-1 text-rose-600 font-medium pt-1 text-[11px]">
                  <p>* ช่องคิดค่าที่จอด / = คิดค่าที่จอด หากไม่มีการคิดค่าที่จอดให้เว้นว่าง</p>
                  <p>* ใส่ข้อมูลเฉพาะห้องที่มียานพาหนะ</p>
                  <p>* ประเภทรถจะมี 2 ประเภทเท่านั้น = รถยนต์ / รถจักรยานยนต์</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold flex items-center gap-1.5 transition-colors cursor-pointer"
                >
                  <Download className="w-4 h-4" /> ดาวน์โหลดไฟล์แบบฟอร์ม (.csv)
                </button>
                <button
                  type="button"
                  onClick={() => setShowExcelTemplateModal(false)}
                  className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg font-semibold transition-colors cursor-pointer"
                >
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
